import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail, Lock, ArrowRight, ArrowLeft } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canManagePlatform } from '../utils/permissions';
import Footer from '../components/Footer';

const Login = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    
    setError('');
    setIsLoading(true);
    
    try {
      const result = await login(email, password);
      if (result.success) {
        if (canManagePlatform(result.user || { email })) {
          navigate('/admin');
        } else {
          navigate(from, { replace: true });
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-500 hover:text-brand-indigo font-bold text-sm transition-all mb-6 group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 md:p-12 rounded-3xl border border-gray-100 shadow-2xl shadow-indigo-100/30 flex flex-col items-center md:items-start text-center md:text-left"
          >
            <div className="w-16 h-16 bg-brand-indigo rounded-2xl flex items-center justify-center text-white mb-8 shadow-xl shadow-brand-indigo/20">
              <Shield size={32} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-3">Welcome Back</h1>
            <p className="text-gray-500 font-medium mb-10">Sign in to your account to continue.</p>

            <form onSubmit={handleSubmit} className="space-y-4 w-full">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input 
                  type="email" 
                  placeholder="Email address" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input 
                  type="password" 
                  placeholder="Password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all"
                />
              </div>
              
              <div className="flex justify-end px-2">
                <button type="button" className="text-xs font-bold text-brand-indigo hover:underline">Forgot password?</button>
              </div>

              {error && <p className="text-red-500 text-sm font-bold px-2">{error}</p>}

              <button 
                type="submit"
                disabled={isLoading}
                className={`w-full ${isLoading ? 'bg-brand-indigo/70 cursor-not-allowed' : 'bg-brand-indigo hover:bg-brand-indigo/90'} text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-indigo/10 flex items-center justify-center gap-2 transition-all transform hover:scale-[1.01] active:scale-[0.99]`}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-8 border-t border-gray-50 text-center w-full">
              <p className="text-sm text-gray-500 font-bold">
                Don't have an account? 
                <Link to="/signup" state={{ from: location.state?.from }} className="text-brand-indigo hover:underline ml-1">Create one</Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;
